import { GridCallbackDetails, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useCallback, useState } from 'react';
import { ServerSideProps } from 'shared/types';
import { toBackendFilterModel } from 'shared/utility/dataGrid';
import { TableDataRequest } from 'types/backend';
import { useSkippableDebounce } from './useSkippableDebounce';

interface UseTableDataRequestProps {
    initialTableDataRequest?: TableDataRequest;
    persistentInitialTableDataRequest?: TableDataRequest;
}

const defaultTableDataRequest: TableDataRequest = {
    PageNumber: 0,
    PageSize: 15,
};

export function useTableDataRequest({
    initialTableDataRequest = defaultTableDataRequest,
    persistentInitialTableDataRequest,
}: UseTableDataRequestProps): {
    debouncedTableDataRequest: TableDataRequest;
    setTableDataRequest: React.Dispatch<React.SetStateAction<TableDataRequest>>;
    serverSideProps: ServerSideProps;
    dontDebounceTableDataRequest: (value: TableDataRequest) => void;
    resetPageNumber: () => void;
} {
    const [tableDataRequest, setTableDataRequest] = useState<TableDataRequest>(initialTableDataRequest);

    const { debouncedValue: debouncedTableDataRequest, dontDebounce } = useSkippableDebounce(tableDataRequest, 1000);

    const onFilterModelChange = useCallback(
        (filterModel: GridFilterModel) => {
            const converted = toBackendFilterModel(filterModel);
            const newFilterModel = {
                ...converted,
                items: [...converted.items, ...(persistentInitialTableDataRequest?.FilterModel?.items || [])],
            };
            setTableDataRequest((prevState) => ({ ...prevState, FilterModel: newFilterModel }));
        },
        [setTableDataRequest, persistentInitialTableDataRequest?.FilterModel?.items]
    );

    const onPaginationModelChange = useCallback(
        (paginationModel: GridPaginationModel, details: GridCallbackDetails) =>
            setTableDataRequest((prevState) => {
                const pageSizeChanged = paginationModel.pageSize !== prevState.PageSize;
                return {
                    ...prevState,
                    PageNumber: pageSizeChanged ? 0 : paginationModel.page,
                    PageSize: paginationModel.pageSize,
                };
            }),
        [setTableDataRequest]
    );

    const onSortModelChange = useCallback(
        (sortModel: GridSortModel, details: GridCallbackDetails) =>
            setTableDataRequest((prevState) => ({ ...prevState, SortModel: sortModel })),
        [setTableDataRequest]
    );

    const dontDebounceTableDataRequest = useCallback(
        (value: TableDataRequest) => {
            setTableDataRequest(value);
            dontDebounce(value);
        },
        [dontDebounce]
    );

    const resetPageNumber = useCallback(() => {
        dontDebounceTableDataRequest({ ...tableDataRequest, PageNumber: 0 });
    }, [tableDataRequest, dontDebounceTableDataRequest]);

    return {
        resetPageNumber,
        setTableDataRequest,
        debouncedTableDataRequest,
        dontDebounceTableDataRequest,
        serverSideProps: {
            onFilterModelChange,
            onPaginationModelChange,
            paginationModel: {
                page: debouncedTableDataRequest.PageNumber ?? 0,
                pageSize: debouncedTableDataRequest.PageSize ?? 15,
            },
            sortModel: debouncedTableDataRequest.SortModel,
            onSortModelChange,
        },
    };
}
